'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const CaseStudyShowcase = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('financial-services');

  const caseStudies = {
    'financial-services': {
      title: 'Global Bank Transformation',
      challenge: 'Leadership misalignment and cultural resistance blocking AI adoption across 50,000 employees',
      solution: 'Implemented human-centered change management framework with executive alignment workshops and phased rollout strategy',
      results: [
        '40% increase in AI tool adoption within 6 months',
        '$18M in operational cost savings',
        '85% employee satisfaction with transformation process',
      ],
      timeline: '12 months',
    },
    'healthcare': {
      title: 'Healthcare Provider AI Integration',
      challenge: 'Complex regulatory environment and clinician skepticism hindering AI implementation',
      solution: 'Developed compliance-first methodology with clinical champion program and evidence-based change approach',
      results: [
        '60% reduction in administrative burden',
        '3x faster patient processing times',
        '95% clinician approval rating',
      ],
      timeline: '9 months',
    },
    'manufacturing': {
      title: 'Manufacturing Excellence Program',
      challenge: 'Siloed operations and skills gap preventing AI-driven optimization',
      solution: 'Cross-functional transformation program with upskilling initiatives and collaborative governance model',
      results: [
        '35% improvement in production efficiency',
        '$12M annual cost reduction',
        '70% reduction in quality defects',
      ],
      timeline: '10 months',
    },
  };

  const industries = [
    { id: 'financial-services', label: 'Financial Services', icon: 'BanknotesIcon' },
    { id: 'healthcare', label: 'Healthcare', icon: 'HeartIcon' },
    { id: 'manufacturing', label: 'Manufacturing', icon: 'CogIcon' },
  ];

  const currentCase = caseStudies[selectedIndustry as keyof typeof caseStudies];

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
              Industry Success Stories
            </h2>
            <p className="text-xl text-muted-foreground font-body">
              Explore how organizations in your industry achieved transformation success.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {industries.map((industry) => (
              <button
                key={industry.id}
                onClick={() => setSelectedIndustry(industry.id)}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  selectedIndustry === industry.id
                    ? 'bg-primary text-white shadow-strong'
                    : 'bg-card text-foreground border border-border hover:border-primary hover:bg-primary/5'
                }`}
              >
                <Icon name={industry.icon} size={20} variant="solid" />
                {industry.label}
              </button>
            ))}
          </div>

          <div className="bg-card rounded-2xl p-8 lg:p-12 shadow-strong">
            <h3 className="text-2xl md:text-3xl font-bold font-headline text-primary mb-8">
              {currentCase.title}
            </h3>

            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="ExclamationTriangleIcon" size={24} variant="solid" className="text-warning" />
                  <h4 className="text-lg font-semibold text-foreground">Challenge</h4>
                </div>
                <p className="text-foreground font-body leading-relaxed pl-8">
                  {currentCase.challenge}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="LightBulbIcon" size={24} variant="solid" className="text-primary" />
                  <h4 className="text-lg font-semibold text-foreground">Solution</h4>
                </div>
                <p className="text-foreground font-body leading-relaxed pl-8">
                  {currentCase.solution}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="ChartBarIcon" size={24} variant="solid" className="text-success" />
                  <h4 className="text-lg font-semibold text-foreground">Results</h4>
                </div>
                <ul className="space-y-2 pl-8">
                  {currentCase.results.map((result, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-body">{result}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-border">
                <Icon name="ClockIcon" size={20} variant="solid" className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground font-semibold">
                  Timeline: {currentCase.timeline}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyShowcase;